interface ErrorHandlerOptions {
  showToast?: boolean;
  customMessage?: string;
  logToConsole?: boolean;
  duration?: number;
}

const ERROR_MESSAGES: Record<number, string> = {
  400: 'Oops! Something went wrong with your request. Please try again.',
  401: 'Your session has expired. Please sign in again to continue.',
  403: 'Sorry, you don\'t have permission to do that.',
  404: 'We couldn\'t find what you\'re looking for. It might have been moved or deleted.',
  408: 'This is taking longer than expected. Please try again.',
  409: 'This item already exists. Please check and try again.',
  422: 'Please check your information and try again.',
  429: 'You\'re doing that too quickly! Please wait a moment and try again.',
  500: 'Something went wrong on our end. We\'re working to fix it!',
  502: 'Our service is temporarily unavailable. Please try again in a moment.',
  503: 'We\'re performing maintenance. Please try again shortly.',
  504: 'This is taking too long. Please try again.'
};

class ErrorHandler {
  private static instance: ErrorHandler;
  private toastContainer: HTMLElement | null = null;

  private constructor() {
    this.createToastContainer();
  }

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  private createToastContainer() {
    if (typeof window === 'undefined') return;
    
    if (!document.getElementById('error-toast-container')) {
      const container = document.createElement('div');
      container.id = 'error-toast-container';
      container.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        pointer-events: none;
      `;
      document.body.appendChild(container);
      this.toastContainer = container;
    } else {
      this.toastContainer = document.getElementById('error-toast-container');
    }
  }

  private showToast(message: string, duration: number = 4000) {
    if (!this.toastContainer) return;

    const toast = document.createElement('div');
    toast.style.cssText = `
      background: #ffffff;
      border: 1px solid #f5f5f5;
      border-left: 4px solid #ff4757;
      color: #2f3542;
      padding: 16px 20px;
      border-radius: 8px;
      margin-bottom: 12px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.4;
      max-width: 400px;
      word-wrap: break-word;
      pointer-events: auto;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.3s ease-out;
      position: relative;
      cursor: pointer;
    `;
    
    const icon = document.createElement('div');
    icon.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #ff4757;
      color: white;
      font-size: 12px;
      font-weight: 600;
      margin-right: 12px;
      flex-shrink: 0;
    `;
    icon.textContent = '!';
    
    const content = document.createElement('div');
    content.style.cssText = `
      display: flex;
      align-items: flex-start;
      gap: 12px;
    `;
    
    const messageSpan = document.createElement('div');
    messageSpan.style.cssText = `
      flex: 1;
      font-weight: 500;
      color: #2f3542;
    `;
    messageSpan.textContent = message;
    
    const closeBtn = document.createElement('button');
    closeBtn.style.cssText = `
      background: none;
      border: none;
      color: #a4b0be;
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;
      flex-shrink: 0;
    `;
    closeBtn.innerHTML = '×';
    closeBtn.onmouseover = () => closeBtn.style.color = '#2f3542';
    closeBtn.onmouseout = () => closeBtn.style.color = '#a4b0be';
    
    content.appendChild(icon);
    content.appendChild(messageSpan);
    content.appendChild(closeBtn);
    toast.appendChild(content);

    const removeToast = () => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (this.toastContainer?.contains(toast)) {
          this.toastContainer.removeChild(toast);
        }
      }, 300);
    };

    closeBtn.onclick = removeToast;
    toast.onclick = removeToast;

    this.toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(0)';
    }, 50);

    setTimeout(removeToast, duration);
  }

  showSuccessToast(message: string, duration: number = 3000) {
    if (!this.toastContainer) return;

    const toast = document.createElement('div');
    toast.style.cssText = `
      background: #ffffff;
      border: 1px solid #f5f5f5;
      border-left: 4px solid #2ed573;
      color: #2f3542;
      padding: 16px 20px;
      border-radius: 8px;
      margin-bottom: 12px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.4;
      max-width: 400px;
      word-wrap: break-word;
      pointer-events: auto;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.3s ease-out;
      position: relative;
      cursor: pointer;
    `;
    
    const icon = document.createElement('div');
    icon.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #2ed573;
      color: white;
      font-size: 12px;
      font-weight: 600;
      margin-right: 12px;
      flex-shrink: 0;
    `;
    icon.textContent = '✓';
    
    const content = document.createElement('div');
    content.style.cssText = `
      display: flex;
      align-items: flex-start;
      gap: 12px;
    `;
    
    const messageSpan = document.createElement('div');
    messageSpan.style.cssText = `
      flex: 1;
      font-weight: 500;
      color: #2f3542;
    `;
    messageSpan.textContent = message;
    
    const closeBtn = document.createElement('button');
    closeBtn.style.cssText = `
      background: none;
      border: none;
      color: #a4b0be;
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s ease;
      flex-shrink: 0;
    `;
    closeBtn.innerHTML = '×';
    closeBtn.onmouseover = () => closeBtn.style.color = '#2f3542';
    closeBtn.onmouseout = () => closeBtn.style.color = '#a4b0be';
    
    content.appendChild(icon);
    content.appendChild(messageSpan);
    content.appendChild(closeBtn);
    toast.appendChild(content);

    const removeToast = () => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (this.toastContainer?.contains(toast)) {
          this.toastContainer.removeChild(toast);
        }
      }, 300);
    };

    closeBtn.onclick = removeToast;
    toast.onclick = removeToast;

    this.toastContainer.appendChild(toast);

    setTimeout(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateX(0)';
    }, 50);

    setTimeout(removeToast, duration);
  }

  handleError(error: any, options: ErrorHandlerOptions = {}) {
    const {
      showToast = true,
      customMessage,
      logToConsole = true,
      duration = 4000
    } = options;

    let errorMessage = customMessage;
    
    if (!errorMessage) {
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error?.response?.data?.message) {
        const data = error.response.data;
        if (data.errors && typeof data.errors === 'string') {
          errorMessage = data.errors;
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (typeof errors === 'string') {
          errorMessage = errors;
        } else if (Array.isArray(errors)) {
          errorMessage = errors.join(', ');
        } else if (typeof errors === 'object') {
          errorMessage = Object.values(errors).flat().join(', ');
        }
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.statusText) {
        errorMessage = error.response.statusText;
      } else if (error?.code && typeof error.code === 'number') {
        const statusCode = error.code;
        errorMessage = ERROR_MESSAGES[statusCode] || 'Something unexpected happened. Please try again.';
      } else if (error?.status && typeof error.status === 'number') {
        const statusCode = error.status;
        errorMessage = ERROR_MESSAGES[statusCode] || 'Something unexpected happened. Please try again.';
      } else if (error?.response?.status) {
        const statusCode = error.response.status;
        errorMessage = ERROR_MESSAGES[statusCode] || 'Something unexpected happened. Please try again.';
      } else {
        errorMessage = 'Oops! Something went wrong. Please try again.';
      }
    }

    if (logToConsole) {
      console.error('Error handled:', error);
    }

    if (showToast && errorMessage) {
      this.showToast(errorMessage, duration);
    }

    return errorMessage || 'Unknown error';
  }
}

export const errorHandler = ErrorHandler.getInstance();

export const handleApiError = (error: any, customMessage?: string) => {
  return errorHandler.handleError(error, { customMessage });
};

export const showSuccessToast = (message: string, duration?: number) => {
  return errorHandler.showSuccessToast(message, duration);
};

export default errorHandler;
